import { useCallback, useEffect, useRef, useState } from "react";

import { checkEmailVerified } from "../api";
import { clearDraft, hasProgress, loadDraft, saveDraft } from "../lib/draft";
import { EMPTY_VALUES } from "./steps";

const SAVE_DELAY_MS = 500;

// Restores an unfinished application for this role on mount and keeps it
// saved while the person types. A restored email only counts as confirmed if
// the server still has it verified (its window is one hour).
export default function useApplyDraft(slug, form, { demo = false } = {}) {
  const [restoredAt, setRestoredAt] = useState(null);
  const [maxReached, setMaxReached] = useState(0);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [emailExpired, setEmailExpired] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [ready, setReady] = useState(false);
  const meta = useRef({ maxReached: 0, verifiedEmail: null });

  useEffect(() => {
    const draft = loadDraft(slug);
    if (hasProgress(draft)) {
      form.reset({ ...EMPTY_VALUES, ...draft.values });
      setRestoredAt(draft.savedAt);
      setMaxReached(draft.maxReached || 0);
      meta.current.maxReached = draft.maxReached || 0;
      if (draft.verifiedEmail) {
        // The sample role never talks to the server, so its email stays confirmed.
        const check = demo ? Promise.resolve(true) : checkEmailVerified(draft.verifiedEmail);
        check.then((stillVerified) => {
          if (stillVerified) {
            meta.current.verifiedEmail = draft.verifiedEmail;
            setVerifiedEmail(draft.verifiedEmail);
          } else {
            setEmailExpired(true);
          }
        });
      }
    }
    setReady(true);
    // form is stable for the component's life; run once per role.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const persist = useCallback(() => {
    // A save queued just before the application was sent must not bring
    // the draft back.
    if (meta.current.sent) return;
    const now = Date.now();
    saveDraft(slug, { values: form.getValues(), ...meta.current }, now);
    setSavedAt(now);
  }, [slug, form]);

  useEffect(() => {
    if (!ready) return undefined;
    let timer = null;
    const flush = () => {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
      persist();
    };
    const subscription = form.watch(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        persist();
      }, SAVE_DELAY_MS);
    });
    // Leaving straight after typing (Save and exit, closing the tab) still
    // keeps the last keystrokes.
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
      subscription.unsubscribe();
    };
  }, [ready, form, persist]);

  const reach = useCallback(
    (index) => {
      if (index <= meta.current.maxReached) return;
      meta.current.maxReached = index;
      setMaxReached(index);
      persist();
    },
    [persist],
  );

  const rememberVerified = useCallback(
    (email) => {
      meta.current.verifiedEmail = email;
      setEmailExpired(false);
      persist();
    },
    [persist],
  );

  const startOver = useCallback(() => {
    clearDraft(slug);
    meta.current = { maxReached: 0, verifiedEmail: null };
    form.reset(EMPTY_VALUES);
    setMaxReached(0);
    setVerifiedEmail(null);
    setRestoredAt(null);
    setSavedAt(null);
    setEmailExpired(false);
  }, [slug, form]);

  return {
    ready,
    restoredAt,
    dismissRestored: () => setRestoredAt(null),
    maxReached,
    reach,
    verifiedEmail,
    rememberVerified,
    emailExpired,
    savedAt,
    startOver,
    discard: () => {
      meta.current.sent = true;
      clearDraft(slug);
    },
  };
}
